// src/utils/locale.js
import app from 'configs/appResolver';

// normaliza 'en-us' → 'en-US'
export const toBCP47 = (lang = '') =>
  lang
    .toString()
    .trim()
    .toLowerCase()
    .replace('_', '-')
    .split('-')
    .map((seg, i) => (i === 0 ? seg : seg.toUpperCase()))
    .join('-');

// normaliza 'US' → 'US'
export const toRegion = (loc = '') =>
  loc.toString().trim().toLowerCase().replace('_', '-').split('-').pop().toUpperCase();

// mapeia app.LOCALE → país ISO2 aceito por phone-input libs
export const appLocaleToPhoneCountry = (loc = app.LOCALE) => {
  const r = toRegion(loc || 'US'); // 'US', 'BR', ...
  // ajustes pontuais se precisar
  const map = {
    UK: 'GB',
  };
  return map[r] || r;
};

// chaves usadas no storage
const LS_LANG_KEY = 'app.lang';

// idioma padrão (primeira carga): localStorage -> app.LANGUAGE
export const resolveInitialLanguage = () => {
  const saved = window.localStorage.getItem(LS_LANG_KEY);
  return saved || toBCP47(app.LANGUAGE || 'en-us');
};

// salva idioma escolhido pelo usuário
export const persistLanguage = lang => {
  if (!lang) return;
  window.localStorage.setItem(LS_LANG_KEY, toBCP47(lang));
};

// cabeçalhos padrão com base no app
export const buildLocaleHeaders = () => {
  const acceptLanguage = window.localStorage.getItem(LS_LANG_KEY) || toBCP47(app.LANGUAGE);
  const localecode = toRegion(app.LOCALE || 'US');
  return {
    'accept-language': acceptLanguage, // e.g. en-US, pt-BR
    'x-locale': localecode, // e.g. US, BR
  };
};

export const normalizeCountryISO = (countryCode, codeType = 'bcp') => {
  // mapping: country -> default language
  const countryToLang = {
    AC: 'en',
    AD: 'ca',
    AE: 'ar',
    AF: 'fa',
    AG: 'en',
    AI: 'en',
    AL: 'sq',
    AM: 'hy',
    AO: 'pt',
    AR: 'es',
    AS: 'en',
    AT: 'de',
    AU: 'en',
    AW: 'nl',
    AX: 'sv',
    AZ: 'az',
    BA: 'bs',
    BB: 'en',
    BD: 'bn',
    BE: 'nl',
    BF: 'fr',
    BG: 'bg',
    BH: 'ar',
    BI: 'fr',
    BJ: 'fr',
    BL: 'fr',
    BM: 'en',
    BN: 'ms',
    BO: 'es',
    BQ: 'nl',
    BR: 'pt',
    BS: 'en',
    BT: 'dz',
    BW: 'en',
    BY: 'be',
    BZ: 'en',
    CA: 'en',
    CC: 'en',
    CD: 'fr',
    CF: 'fr',
    CG: 'fr',
    CH: 'de',
    CI: 'fr',
    CK: 'en',
    CL: 'es',
    CM: 'fr',
    CN: 'zh',
    CO: 'es',
    CR: 'es',
    CU: 'es',
    CV: 'pt',
    CW: 'nl',
    CX: 'en',
    CY: 'el',
    CZ: 'cs',
    DE: 'de',
    DJ: 'fr',
    DK: 'da',
    DM: 'en',
    DO: 'es',
    DZ: 'ar',
    EC: 'es',
    EE: 'et',
    EG: 'ar',
    EH: 'ar',
    ER: 'ti',
    ES: 'es',
    ET: 'am',
    FI: 'fi',
    FJ: 'en',
    FK: 'en',
    FM: 'en',
    FO: 'fo',
    FR: 'fr',
    GA: 'fr',
    GB: 'en',
    GD: 'en',
    GE: 'ka',
    GF: 'fr',
    GG: 'en',
    GH: 'en',
    GI: 'en',
    GL: 'kl',
    GM: 'en',
    GN: 'fr',
    GP: 'fr',
    GQ: 'es',
    GR: 'el',
    GT: 'es',
    GU: 'en',
    GW: 'pt',
    GY: 'en',
    HK: 'zh',
    HN: 'es',
    HR: 'hr',
    HT: 'fr',
    HU: 'hu',
    ID: 'id',
    IE: 'en',
    IL: 'he',
    IM: 'en',
    IN: 'hi',
    IO: 'en',
    IQ: 'ar',
    IR: 'fa',
    IS: 'is',
    IT: 'it',
    JE: 'en',
    JM: 'en',
    JO: 'ar',
    JP: 'ja',
    KE: 'sw',
    KG: 'ky',
    KH: 'km',
    KI: 'en',
    KM: 'ar',
    KN: 'en',
    KP: 'ko',
    KR: 'ko',
    KW: 'ar',
    KY: 'en',
    KZ: 'kk',
    LA: 'lo',
    LB: 'ar',
    LC: 'en',
    LI: 'de',
    LK: 'si',
    LR: 'en',
    LS: 'en',
    LT: 'lt',
    LU: 'fr',
    LV: 'lv',
    LY: 'ar',
    MA: 'ar',
    MC: 'fr',
    MD: 'ro',
    ME: 'sr',
    MF: 'fr',
    MG: 'mg',
    MH: 'en',
    MK: 'mk',
    ML: 'fr',
    MM: 'my',
    MN: 'mn',
    MO: 'zh',
    MP: 'en',
    MQ: 'fr',
    MR: 'ar',
    MS: 'en',
    MT: 'mt',
    MU: 'en',
    MV: 'dv',
    MW: 'en',
    MX: 'es',
    MY: 'ms',
    MZ: 'pt',
    NA: 'en',
    NC: 'fr',
    NE: 'fr',
    NF: 'en',
    NG: 'en',
    NI: 'es',
    NL: 'nl',
    NO: 'no',
    NP: 'ne',
    NR: 'en',
    NU: 'en',
    NZ: 'en',
    OM: 'ar',
    PA: 'es',
    PE: 'es',
    PF: 'fr',
    PG: 'en',
    PH: 'en',
    PK: 'ur',
    PL: 'pl',
    PM: 'fr',
    PR: 'es',
    PS: 'ar',
    PT: 'pt',
    PW: 'en',
    PY: 'es',
    QA: 'ar',
    RE: 'fr',
    RO: 'ro',
    RS: 'sr',
    RU: 'ru',
    RW: 'rw',
    SA: 'ar',
    SB: 'en',
    SC: 'fr',
    SD: 'ar',
    SE: 'sv',
    SG: 'en',
    SH: 'en',
    SI: 'sl',
    SJ: 'no',
    SK: 'sk',
    SL: 'en',
    SM: 'it',
    SN: 'fr',
    SO: 'so',
    SR: 'nl',
    SS: 'en',
    ST: 'pt',
    SV: 'es',
    SX: 'nl',
    SY: 'ar',
    SZ: 'en',
    TA: 'en',
    TC: 'en',
    TD: 'fr',
    TG: 'fr',
    TH: 'th',
    TJ: 'tg',
    TK: 'en',
    TL: 'pt',
    TM: 'tk',
    TN: 'ar',
    TO: 'en',
    TR: 'tr',
    TT: 'en',
    TV: 'en',
    TW: 'zh',
    TZ: 'sw',
    UA: 'uk',
    UG: 'en',
    US: 'en',
    UY: 'es',
    UZ: 'uz',
    VA: 'it',
    VC: 'en',
    VE: 'es',
    VG: 'en',
    VI: 'en',
    VN: 'vi',
    VU: 'en',
    WF: 'fr',
    WS: 'sm',
    XK: 'sq',
    YE: 'ar',
    YT: 'fr',
    ZA: 'en',
    ZM: 'en',
    ZW: 'en',
  };

  // mapping: language -> default country
  const langToCountry = {
    ar: 'SA',
    am: 'ET',
    az: 'AZ',
    be: 'BY',
    bg: 'BG',
    bn: 'BD',
    bs: 'BA',
    ca: 'AD',
    cs: 'CZ',
    da: 'DK',
    de: 'DE',
    dv: 'MV',
    dz: 'BT',
    el: 'GR',
    en: 'US',
    es: 'ES',
    et: 'EE',
    fa: 'IR',
    fi: 'FI',
    fo: 'FO',
    fr: 'FR',
    he: 'IL',
    hi: 'IN',
    hr: 'HR',
    hu: 'HU',
    hy: 'AM',
    id: 'ID',
    is: 'IS',
    it: 'IT',
    ja: 'JP',
    ka: 'GE',
    kk: 'KZ',
    kl: 'GL',
    km: 'KH',
    ko: 'KR',
    ky: 'KG',
    lo: 'LA',
    lt: 'LT',
    lv: 'LV',
    mg: 'MG',
    mk: 'MK',
    mn: 'MN',
    ms: 'MY',
    mt: 'MT',
    my: 'MM',
    ne: 'NP',
    nl: 'NL',
    no: 'NO',
    pl: 'PL',
    pt: 'BR',
    ro: 'RO',
    ru: 'RU',
    rw: 'RW',
    si: 'LK',
    sk: 'SK',
    sl: 'SI',
    sm: 'WS',
    so: 'SO',
    sq: 'AL',
    sr: 'RS',
    sv: 'SE',
    sw: 'KE',
    tg: 'TJ',
    th: 'TH',
    ti: 'ER',
    tk: 'TM',
    tr: 'TR',
    uk: 'UA',
    ur: 'PK',
    uz: 'UZ',
    vi: 'VN',
    zh: 'CN',
  };

  const normalized = countryCode?.trim().toLowerCase() || '';

  if (!normalized) return null;

  let lang, country;

  if (normalized.includes('-') || normalized.includes('_')) {
    // format: 'pt-BR' or 'pt_BR'
    const [l, c] = normalized.split(/[-_]/);
    lang = l;
    country = c.toUpperCase();
  } else if (normalized.length === 2) {
    const upper = normalized.toUpperCase();

    if (countryToLang[upper]) {
      // it's a country code
      country = upper;
      lang = countryToLang[upper];
    } else if (langToCountry[normalized]) {
      // it's a language code
      lang = normalized;
      country = langToCountry[normalized];
    } else {
      return null;
    }
  } else {
    return null;
  }

  switch (codeType) {
    case 'iso':
      return lang;
    case 'iso2':
      return country;
    case 'bcp':
    default:
      return `${lang}-${country}`;
  }
};
